import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const SafeKaTeX = ({ latex }) => {
  try {
    return <BlockMath math={latex} />;
  } catch (error) {
    console.warn('KaTeX rendering error:', error);
    return <div className="katex-error">Mathematical expression</div>;
  }
};

function LandingPage() {
  const [currentDemo, setCurrentDemo] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [displayedInput, setDisplayedInput] = useState('');
  const [showOutput, setShowOutput] = useState(false);

  const demoExamples = [
    {
      title: "Basic Calculus",
      input: "eval(derivative(pow(x, 3), x))",
      output: "3x²",
      latex: "3x^2",
      useKatex: false,
      description: "Compute derivatives symbolically"
    },
    {
      title: "Matrix Creation",
      input: "matrix(covector(1, 2, 3), covector(4, 5, 6))",
      output: "Matrix display",
      latex: "\\begin{bmatrix} 1 & 2 & 3 \\\\ 4 & 5 & 6 \\end{bmatrix}",
      useKatex: true,
      description: "Create and display matrices"
    },
    {
      title: "Matrix Transpose",
      input: "eval(transpose(matrix(covector(1, 2, 3), covector(4, 5, 6))))",
      output: "Transposed matrix",
      latex: "\\begin{bmatrix} 1 & 4 \\\\ 2 & 5 \\\\ 3 & 6 \\end{bmatrix}",
      useKatex: true,
      description: "Transpose matrices easily"
    },
    {
      title: "Limits & Analysis",
      input: "eval(limit(div(sin(x), x), x, 0))",
      output: "1",
      latex: "1",
      useKatex: false,
      description: "Evaluate complex limits"
    }
  ];

  // Typing animation effect
  useEffect(() => {
    const example = demoExamples[currentDemo];
    setIsTyping(true);
    setShowOutput(false);
    setDisplayedInput('');
    
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index < example.input.length) {
        setDisplayedInput(example.input.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
        setTimeout(() => setShowOutput(true), 500);
      }
    }, 80); // Changed from 50ms to 80ms (slower typing)
  
    return () => clearInterval(typingInterval);
  }, [currentDemo]);
  
  // Auto-cycle through examples
  useEffect(() => {
    const cycleInterval = setInterval(() => {
      setCurrentDemo((prev) => (prev + 1) % demoExamples.length);
    }, 6000); // Changed from 4000ms to 6000ms (6 seconds)
  
    return () => clearInterval(cycleInterval);
  }, []);

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <h1 className="hero-title">
            MathLaTech
            <span className="subtitle">Executable Mathematics Platform</span>
          </h1>
          <p className="hero-text">
            Perform symbolic and numeric computations while simultaneously rendering to beautiful LaTeX. 
            Write mathematical expressions in our intuitive ./math language, then export your work to PDF 
            or copy the LaTeX for reuse. Perfect for educators, students, and researchers.
          </p>
          <div className="hero-buttons">
            <Link to="/docs" className="btn btn-primary">
              📚 Documentation
            </Link>
            <Link to="/about" className="btn btn-secondary">
              📖 Learn More
            </Link>
            <a 
              href="https://www.buymeacoffee.com/mathlatech" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
            >
              💝 Donate
            </a>
          </div>
          
          {/* New Launch MathLaTech Button */}
          <div className="hero-launch-button">
            <Link to="/app" className="btn btn-primary btn-large">
              🚀 Launch MathLaTech
            </Link>
            <Link to="/notebook" className="btn btn-primary btn-large">
              📓 Launch Notebook
            </Link>
          </div>
        </div>
      </section>

      {/* Live Demo Section - Only keep this one */}
      <section className="demo-section">
        <div className="container">
          <h2 className="section-title">Try It Live</h2>
          <p className="demo-subtitle">Watch our mathematical DSL in action</p>
          
          <div className="demo-container">
            <div className="demo-panel input-panel">
              <div className="panel-header">
                <h3>🔢 ./math Input</h3>
                <div className="demo-indicators">
                  {demoExamples.map((_, index) => (
                    <button
                      key={index}
                      className={`indicator ${index === currentDemo ? 'active' : ''}`}
                      onClick={() => setCurrentDemo(index)}
                    />
                  ))}
                </div>
              </div>
              <div className="demo-editor">
                <div className="demo-input">
                  <span className="prompt">{'>'}</span>
                  <span className="typed-text">{displayedInput}</span>
                  {isTyping && <span className="cursor">|</span>}
                </div>
              </div>
              <div className="demo-info">
                <h4>{demoExamples[currentDemo].title}</h4>
                <p>{demoExamples[currentDemo].description}</p>
              </div>
            </div>

            <div className="demo-panel output-panel">
              <div className="panel-header">
                <h3>📊 LaTeX Output</h3>
                <div className="status-indicator">
                  {showOutput ? (
                    <span className="status ready">✓ Ready</span>
                  ) : (
                    <span className="status processing">⚡ Processing</span>
                  )}
                </div>
              </div>
              <div className="demo-output">
                {showOutput ? (
                  <div className="latex-result">
                    <div className="math-display">
                      {demoExamples[currentDemo].useKatex ? (
                        <BlockMath math={demoExamples[currentDemo].latex} />
                      ) : (
                        <div style={{ textAlign: 'center', fontSize: '1.2em' }}>
                          {demoExamples[currentDemo].output}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <span>Computing...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="demo-controls">
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentDemo((prev) => (prev - 1 + demoExamples.length) % demoExamples.length)}
            >
              ← Previous
            </button>
            <Link to="/app" className="btn btn-primary">
              🚀 Launch App →
            </Link>
            <button 
              className="btn btn-secondary"
              onClick={() => setCurrentDemo((prev) => (prev + 1) % demoExamples.length)}
            >
              Next →
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Why Choose MathLaTech?</h2>
          <div className="features-grid">
            <div className="feature">
              <div className="feature-icon">⚡</div>
              <h3>Fast & Intuitive</h3>
              <p>Write mathematical expressions in our simple ./math syntax and get instant LaTeX output.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">🔗</div>
              <h3>Function Composition</h3>
              <p>Since ./math is built on function composition, seamlessly integrate with backends written in Python, Julia, C++, or any language as function calls.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">🎯</div>
              <h3>Precise Control</h3>
              <p>Fine-tune your mathematical notation with advanced formatting and symbolic computation.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">🌐</div>
              <h3>Web-based</h3>
              <p>No installation required. Access your mathematical tools from any modern web browser.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">📄</div>
              <h3>Academic Output</h3>
              <p>Export beautiful PDFs or copy LaTeX code for reuse. Create publication-ready mathematical documents with ease.</p>
              <div className="feature-preview">
                <small>📋 Sample PDF: <a href="/Bettermath Expressions - MathLatech.pdf" target="_blank">Better Math Expressions Example</a></small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Ready to Transform Your Mathematics?</h2>
          <p>Join educators and students worldwide who are already using MathLaTech.</p>
          <Link to="/app" className="btn btn-primary btn-large">
            🚀 Start MathLaTech Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h4>MathLaTech</h4>
              <p>Executable Mathematics Platform</p>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <a href="mailto:dotslashmath@gmail.com">dotslashmath@gmail.com</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2024 MathLaTech. Beta Version.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;